import { Camera, Brain, FolderOpen, Calculator, Bell, Users, CheckCircle2, XCircle, Home, Wrench, Building2, LayoutGrid, PaintBucket, Layers, Square, LayoutTemplate, DoorClosed, ShieldCheck, ArrowRight, Tag, BadgeCheck } from "lucide-react";

// How It Works — Part 2 (bottom section).
//
// ─────────────────────────────────────────────────────────────────
//  ASSET-FIRST CENTER-COLUMN PROOF (2 — Levi Organizes It)
// ─────────────────────────────────────────────────────────────────
//  Product Owner approved a controlled proof of Emergent Support's
//  recommended asset-first workflow using ONLY the center column of
//  Part 2. All surrounding Part 2 blocks (Old Way, Better Way,
//  Supported Trades, One-Off / Custom Job, Consolidation banner)
//  are TEMPORARILY hidden behind SHOW_FULL_PART2 = false and are
//  preserved verbatim on disk for reference. Flip this constant to
//  true to re-enable the full row once the PO approves.
//
//  Desktop-only proof: the proof section is gated with `hidden
//  lg:block` so mobile is intentionally not affected in this pass.
//
//  Approved Levi asset is used bit-for-bit, no filter, no overlay,
//  no mask, no tint, no crop, no regeneration:
//    /brand/how-it-works/levi_headshot_approved.png
// ─────────────────────────────────────────────────────────────────

const SHOW_FULL_PART2 = false;

const COPY_P2 = {
  en: {
    // Center-column proof copy
    center: {
      badge: "2",
      title: "Levi Organizes It",
      sub: "Captures. Organizes. Keeps you aligned.",
      caps: [
        { Icon: Camera, label: "Captures every detail" },
        { Icon: Brain, label: "Understands the job" },
        { Icon: FolderOpen, label: "Organizes everything" },
        { Icon: Calculator, label: "Applies your pricing" },
        { Icon: Bell, label: "Flags gaps & follows up" },
        { Icon: Users, label: "Keeps everyone aligned" },
      ],
      promise_a: "Your pricing. Levi organizes.",
      promise_b: "Final approval by you.",
    },
    old_sub: "Scattered. Missed. Stressful.",
    better: {
      title: "The Better Way",
      sub: "Organized. Accurate. Profitable.",
      proposal_badge: "PROPOSAL",
      company_tagline: "EST. 2011  ·  LICENSED & INSURED",
      labels: { project: "Project", customer: "Customer", date: "Date", scope: "Scope",
                materials: "Materials", labor: "Labor", prep: "Prep & Cleanup",
                total: "Total Investment" },
      scope_title: "Exterior Repaint — 2‑Story Home",
      scope: [
        "Pressure wash & full surface preparation",
        "Premium acrylic exterior coat (2 coats)",
        "Trim, fascia & soffit paint",
        "Detail work & final walk‑through",
      ],
      date_val: "May 15, 2025",
      outcomes: [
        "Everything organized",
        "Accurate pricing",
        "Professional proposals",
        "Happy customers",
        "More jobs won",
        "More profit",
      ],
      logo_msg: "Your approved logo is used on all documents, including estimates and proposals.",
    },
    // ── Preserved (hidden) legacy full-row copy ─────────────────
    kicker: "HOW IT WORKS",
    headline: "Levi turns job chaos into clear results.",
    lede: "From messy notes and missed details to organized jobs, accurate pricing, and happy customers.",
    step1: { badge: "1", title: "The Old Way", sub: "Too much to track. Easy to miss.",
      bullets: ["Scattered notes","Forgotten follow-ups","Disconnected tools","Missed details","Pricing guesswork","Lost time & profit"] },
    step2: { badge: "2", title: "Levi Organizes It", sub: "Levi captures, understands, and gets it done.",
      caps: [
        { Icon: Camera, label: "Captures every detail" },
        { Icon: Brain, label: "Understands the job" },
        { Icon: FolderOpen, label: "Organizes everything" },
        { Icon: Calculator, label: "Applies your pricing" },
        { Icon: Bell, label: "Flags gaps & follows up" },
        { Icon: Users, label: "Keeps everyone aligned" },
      ],
      promise_a: "Your pricing. Levi organizes.", promise_b: "Final approval by you." },
    step3: { badge: "3", title: "The Better Way", sub: "Clear, professional, and profitable.",
      outcomes: ["Everything organized","Happy customers","Accurate pricing","More jobs won","Professional proposals","More profit"],
      logo_note: "Your approved logo is used on all documents, including estimates and proposals.",
      proposal: {
        company_line1: "VALLEY PAINTING", company_line2: "EST. 2011  ·  LICENSED & INSURED",
        project_label: "Project", project_val: "2405 Maple Ave.",
        date_label: "Date", date_val: "5/15/25",
        customer_label: "Customer", customer_val: "Martha Johnson",
        scope_title: "Exterior Repaint — 2-Story Home",
        scope: ["Pressure wash & surface prep","Premium acrylic exterior coat","Trim, fascia & soffit paint","Detail work & final walk-through"],
        total_label: "Total Investment", total_val: "$6,875.00",
      },
    },
    trades: {
      title: "Supported trades",
      subtitle: "Built for the trades JobClose AI serves today — with more added over time.",
      items: [
        { Icon: Home, label: "Roofing" },
        { Icon: Wrench, label: "Remodel & Handyman" },
        { Icon: Building2, label: "General Construction" },
        { Icon: LayoutGrid, label: "Concrete" },
        { Icon: PaintBucket, label: "Painting" },
        { Icon: Layers, label: "Stucco" },
        { Icon: Square, label: "Drywall" },
        { Icon: LayoutTemplate, label: "Flooring" },
        { Icon: DoorClosed, label: "Windows & Doors" },
      ],
      more: "And more.",
    },
    custom: {
      title: "One-Off / Custom Job",
      body: "For unique or one-off projects, Levi gathers the details, analyzes the job, and recommends a price or range. You review and approve — always.",
      pill: "Levi recommends. You decide. You approve.",
    },
    consolidation: "JobClose AI puts everything in one place so you can focus on what matters most — your customers and your crew.",
  },
};
COPY_P2.es = {
  center: {
    badge: "2",
    title: "Levi Lo Organiza",
    sub: "Captura. Organiza. Mantiene a todos alineados.",
    caps: [
      { Icon: Camera, label: "Captura cada detalle" },
      { Icon: Brain, label: "Entiende el trabajo" },
      { Icon: FolderOpen, label: "Organiza todo" },
      { Icon: Calculator, label: "Aplica tus precios" },
      { Icon: Bell, label: "Detecta huecos y da seguimiento" },
      { Icon: Users, label: "Mantiene a todos alineados" },
    ],
    promise_a: "Tus precios. Levi organiza.",
    promise_b: "Aprobación final tuya.",
  },
  old_sub: "Desordenado. Perdido. Estresante.",
  better: {
    title: "La Mejor Manera",
    sub: "Organizado. Preciso. Rentable.",
    proposal_badge: "PROPUESTA",
    company_tagline: "EST. 2011  ·  LICENCIA Y SEGURO",
    labels: { project: "Proyecto", customer: "Cliente", date: "Fecha", scope: "Alcance",
              materials: "Materiales", labor: "Mano de Obra", prep: "Preparación y Limpieza",
              total: "Inversión Total" },
    scope_title: "Repintado Exterior — Casa de 2 Pisos",
    scope: [
      "Lavado a presión y preparación de superficie",
      "Capa exterior acrílica premium (2 manos)",
      "Pintura de molduras, fascia y sofito",
      "Detalles y recorrido final",
    ],
    date_val: "15 mayo 2025",
    outcomes: [
      "Todo organizado",
      "Precios precisos",
      "Propuestas profesionales",
      "Clientes satisfechos",
      "Más trabajos ganados",
      "Más ganancia",
    ],
    logo_msg: "Tu logo aprobado se usa en todos los documentos, incluyendo estimados y propuestas.",
  },
  kicker: "CÓMO FUNCIONA",
  headline: "Levi transforma el caos del trabajo en resultados claros.",
  lede: "De notas desordenadas y detalles perdidos a proyectos organizados, precios precisos y clientes satisfechos.",
  step1: { badge: "1", title: "La Forma Antigua", sub: "Demasiado para controlar. Fácil de olvidar.",
    bullets: ["Notas dispersas","Seguimientos olvidados","Herramientas desconectadas","Detalles perdidos","Precios a la suerte","Tiempo y ganancias perdidas"] },
  step2: { badge: "2", title: "Levi Lo Organiza", sub: "Levi captura, entiende y lo resuelve.",
    caps: [
      { Icon: Camera, label: "Captura cada detalle" },
      { Icon: Brain, label: "Entiende el trabajo" },
      { Icon: FolderOpen, label: "Organiza todo" },
      { Icon: Calculator, label: "Aplica tus precios" },
      { Icon: Bell, label: "Detecta huecos y da seguimiento" },
      { Icon: Users, label: "Mantiene a todos alineados" },
    ],
    promise_a: "Tus precios. Levi organiza.", promise_b: "Aprobación final tuya." },
  step3: { badge: "3", title: "La Forma Mejor", sub: "Claro, profesional y rentable.",
    outcomes: ["Todo organizado","Clientes satisfechos","Precios precisos","Más trabajos ganados","Propuestas profesionales","Más ganancias"],
    logo_note: "Tu logo aprobado se usa en todos los documentos, incluyendo estimados y propuestas.",
    proposal: {
      company_line1: "VALLEY PAINTING", company_line2: "EST. 2011  ·  LICENCIA Y SEGURO",
      project_label: "Proyecto", project_val: "2405 Maple Ave.",
      date_label: "Fecha", date_val: "15/5/25",
      customer_label: "Cliente", customer_val: "Martha Johnson",
      scope_title: "Repintado Exterior — Casa de 2 Pisos",
      scope: ["Lavado a presión y preparación","Capa exterior acrílica premium","Pintura de molduras, fascia y sofito","Detalles y recorrido final"],
      total_label: "Inversión Total", total_val: "$6,875.00",
    },
  },
  trades: {
    title: "Oficios que apoyamos",
    subtitle: "Diseñado para los oficios que JobClose AI atiende hoy — con más añadidos con el tiempo.",
    items: [
      { Icon: Home, label: "Techado" },
      { Icon: Wrench, label: "Remodelación y Handyman" },
      { Icon: Building2, label: "Construcción General" },
      { Icon: LayoutGrid, label: "Concreto" },
      { Icon: PaintBucket, label: "Pintura" },
      { Icon: Layers, label: "Estuco" },
      { Icon: Square, label: "Drywall" },
      { Icon: LayoutTemplate, label: "Pisos" },
      { Icon: DoorClosed, label: "Ventanas y Puertas" },
    ],
    more: "Y más.",
  },
  custom: {
    title: "Proyecto Único / Especial",
    body: "Para proyectos únicos o especiales, Levi reúne los detalles, analiza el trabajo y recomienda un precio o rango. Tú revisas y apruebas — siempre.",
    pill: "Levi recomienda. Tú decides. Tú apruebas.",
  },
  consolidation: "JobClose AI reúne todo en un solo lugar para que puedas enfocarte en lo que más importa — tus clientes y tu equipo.",
};

export const HowItWorksPart2 = ({ lang = "en" }) => {
  const c = COPY_P2[lang] || COPY_P2.en;
  return SHOW_FULL_PART2
    ? <FullPart2 c={c} />
    : <CenterColumnProof full={c} />;
};

// ─────────────────────────────────────────────────────────────────
//  DESKTOP PROOF · hidden on <lg
//  Continuous navy environment holding the LEFT ZONE (Old Way) and
//  the LOCKED CENTER ZONE (Levi Organizes It). Right zone (Better
//  Way) not yet authorized. Approved Levi + Notebook PNGs are used
//  bit-for-bit — no filter, tint, mask, crop, or regeneration.
// ─────────────────────────────────────────────────────────────────
const CenterColumnProof = ({ full }) => {
  const c = full.center;
  const old = full.step1;
  const leftCaps = c.caps.slice(0, 3);
  const rightCaps = c.caps.slice(3, 6);
  return (
    <section
      className="hidden lg:block relative"
      data-testid="hiw-part2"
      aria-label="How It Works — Old Way + Levi center zone"
    >
      {/* Deep JobClose navy environment */}
      <div className="relative overflow-hidden bg-[#08122C]">
        {/* Restrained soft-blue tech-glow atmosphere (not on Levi) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 62% 48%, rgba(59,130,246,0.28) 0%, rgba(37,99,235,0.14) 35%, rgba(8,18,44,0) 70%)",
          }}
        />
        {/* Bright organized bloom under the RIGHT third — signals order */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-[36%]"
          style={{
            background:
              "radial-gradient(60% 60% at 58% 55%, rgba(191,219,254,0.20) 0%, rgba(96,165,250,0.10) 40%, rgba(8,18,44,0) 75%)",
          }}
        />
        {/* Warm amber undertone under the LEFT third — signals chaos */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-[36%]"
          style={{
            background:
              "radial-gradient(60% 55% at 42% 55%, rgba(217,119,6,0.16) 0%, rgba(180,83,9,0.08) 35%, rgba(8,18,44,0) 72%)",
          }}
        />
        {/* Faint horizontal ambient sweep for depth */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-40"
          style={{
            background:
              "linear-gradient(180deg, rgba(59,130,246,0.10) 0%, rgba(8,18,44,0) 100%)",
          }}
        />
        {/* Subtle scan-line texture (very low opacity) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(180deg, rgba(255,255,255,0.6) 0px, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 4px)",
          }}
        />

        <div className="relative max-w-[1920px] mx-auto px-2 pt-14 pb-16 grid grid-cols-[minmax(0,420px)_minmax(0,1fr)_minmax(0,432px)] items-start gap-2 xl:gap-3">
          {/* ─────── LEFT ZONE · The Old Way ─────── */}
          <div className="min-w-0 justify-self-end w-full max-w-[420px] 2xl:translate-x-[44px]" data-testid="hiw-p2-old-way">
            {/* Header · badge · title · sub */}
            <div className="flex flex-col items-center text-center">
              <span
                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#1F2A44] text-amber-300 text-[18px] font-extrabold ring-4 ring-amber-400/20 shadow-[0_8px_22px_-8px_rgba(217,119,6,0.6)]"
                data-testid="hiw-p2-old-badge"
              >
                {old.badge}
              </span>
              <h2
                className="mt-5 font-display font-extrabold tracking-tight text-white text-[52px] leading-[1.02]"
                data-testid="hiw-p2-old-title"
              >
                {old.title}
              </h2>
              <p
                className="mt-3 text-[18px] font-medium leading-[1.5] max-w-[440px]"
                style={{ color: "rgba(253,224,168,0.82)" }}
                data-testid="hiw-p2-old-sub"
              >
                {full.old_sub}
              </p>
            </div>

            {/* Notebook · dominant · warm ambient bloom BEHIND (never on PNG) */}
            <div className="relative mt-6" data-testid="hiw-p2-old-notebook-frame">
              <div
                aria-hidden="true"
                className="absolute -inset-3 rounded-[28px] blur-2xl opacity-90"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 55%, rgba(251,191,36,0.28) 0%, rgba(217,119,6,0.14) 42%, rgba(8,18,44,0) 78%)",
                }}
              />
              <img
                src="/brand/how-it-works/hiw_part2_notebook.png"
                alt=""
                className="relative block w-full h-auto select-none rounded-2xl shadow-[0_28px_60px_-20px_rgba(0,0,0,0.85)] ring-1 ring-white/5"
                draggable={false}
                loading="eager"
                decoding="sync"
                data-testid="hiw-p2-old-notebook-img"
              />
            </div>

            {/* Problem points · 2-col live text · red-accented icons on warm dark */}
            <ul
              className="mt-8 grid grid-cols-2 gap-x-5 gap-y-4"
              data-testid="hiw-p2-old-bullets"
            >
              {old.bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3"
                  data-testid={`hiw-p2-old-bullet-${i}`}
                >
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-500/12 text-red-400 ring-1 ring-red-500/25 flex-shrink-0">
                    <XCircle size={16} strokeWidth={2.4} />
                  </span>
                  <span className="text-[16px] font-semibold text-white/90 leading-tight">
                    {b}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* ─────── CENTER ZONE · Levi (LOCKED) ─────── */}
          <div
            className="min-w-0 w-full"
            data-testid="hiw-p2-center-proof"
          >
          {/* Header · badge · title · sub — all inside the navy env */}
          <div className="flex flex-col items-center text-center">
            <span
              className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--jc-blue)] text-white text-[18px] font-extrabold shadow-[0_10px_30px_-8px_rgba(59,130,246,0.9)] ring-4 ring-[var(--jc-blue)]/20"
              data-testid="hiw-p2-center-badge"
            >
              {c.badge}
            </span>
            <h2
              className="mt-5 font-display font-extrabold tracking-tight text-white text-[56px] leading-[1.02]"
              data-testid="hiw-p2-center-title"
            >
              {c.title}
            </h2>
            <p
              className="mt-3 text-[18px] font-medium text-white/70 leading-[1.5] max-w-[560px]"
              data-testid="hiw-p2-center-sub"
            >
              {c.sub}
            </p>
          </div>

          {/* Fluid layout · left caps · Levi · right caps.
              CSS Grid with proportional tracks replaces the previous
              fixed 280+620+280 flex with -130 negative margins.
              At every desktop width the three tracks stay in-column
              (no overlap into Old Way / Better Way). At ≥2xl the
              caps are pulled a hair closer to Levi via decorative
              margins so the 1920 approved presentation is preserved. */}
          <div
            className="mt-6 grid items-center justify-items-center gap-1 xl:gap-2"
            style={{
              gridTemplateColumns:
                "clamp(100px, 20%, 220px) minmax(0, 1fr) clamp(100px, 20%, 220px)",
            }}
            data-testid="hiw-p2-center-frame"
          >
            {/* LEFT capabilities · label outer, icon inner (toward Levi) */}
            <ul className="w-full max-w-[280px] space-y-3 xl:space-y-4 relative z-10 justify-self-end lg:-mr-[40px] xl:-mr-[90px] 2xl:-mr-[180px]" data-testid="hiw-p2-center-caps-left">
              {leftCaps.map((cap, i) => (
                <CapFramed key={i} cap={cap} align="right" testid={`hiw-p2-center-cap-${i}`} />
              ))}
            </ul>

            {/* Levi · center · dominant focal point · restrained tech aura */}
            <div className="relative w-full max-w-[620px] min-w-0 flex items-end justify-center">
              {/* Aura behind Levi — never over him */}
              <div
                aria-hidden="true"
                className="absolute inset-0 -z-0"
                style={{
                  background:
                    "radial-gradient(52% 55% at 50% 55%, rgba(96,165,250,0.38) 0%, rgba(37,99,235,0.18) 45%, rgba(8,18,44,0) 75%)",
                }}
              />
              {/* Soft floor accent */}
              <div
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 bottom-2 w-full max-w-[480px] h-[44px] rounded-full blur-2xl opacity-75"
                style={{
                  background:
                    "radial-gradient(50% 100% at 50% 50%, rgba(59,130,246,0.5) 0%, rgba(8,18,44,0) 100%)",
                }}
              />
              <img
                src="/brand/how-it-works/levi_headshot_approved.png"
                alt="Levi"
                className="relative block w-full max-w-[620px] h-auto select-none"
                draggable={false}
                loading="eager"
                decoding="sync"
                data-testid="hiw-p2-center-levi-img"
              />
            </div>

            {/* RIGHT capabilities · icon inner (toward Levi), label outer */}
            <ul className="w-full max-w-[280px] space-y-3 xl:space-y-4 relative z-10 justify-self-start lg:-ml-[40px] xl:-ml-[90px] 2xl:-ml-[180px]" data-testid="hiw-p2-center-caps-right">
              {rightCaps.map((cap, i) => (
                <CapFramed key={i} cap={cap} align="left" testid={`hiw-p2-center-cap-${i + 3}`} />
              ))}
            </ul>
          </div>

          {/* Prominent concluding approval statement · closer to Levi */}
          <div
            className="mt-6 mx-auto max-w-[820px] rounded-2xl bg-[var(--jc-blue)] text-white text-center px-10 py-7 shadow-[0_30px_60px_-20px_rgba(59,130,246,0.55)] ring-1 ring-white/10"
            data-testid="hiw-p2-center-promise"
          >
            <div className="font-display font-extrabold tracking-tight text-[28px] sm:text-[30px] leading-[1.15]">
              {c.promise_a}
            </div>
            <div className="mt-2 text-[17px] sm:text-[18px] font-medium text-white/90 leading-tight">
              {c.promise_b}
            </div>
          </div>
          </div>

          {/* ─────── RIGHT ZONE · The Better Way ─────── */}
          <BetterWayColumn better={full.better} />
        </div>

        {/* Bottom fade back into page white */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,18,44,0) 0%, rgba(8,18,44,0.0) 40%, rgba(255,255,255,0.0) 100%)",
          }}
        />
      </div>
    </section>
  );
};

// Capability row for the framed center layout.
// Emergent Support directive: each label/icon pair is its own
// self-contained flex unit with justify-end / justify-start and an
// 8 px internal gap between text and icon.
const CapFramed = ({ cap, align, testid }) => {
  const Icon = cap.Icon;
  const isLeftGroup = align === "right"; // left group of caps sits to the LEFT of Levi
  const iconTile = (
    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 text-[#93C5FD] ring-1 ring-white/15 backdrop-blur-sm flex-shrink-0 shadow-[0_8px_22px_-8px_rgba(59,130,246,0.7)]">
      <Icon size={22} strokeWidth={2.2} />
    </span>
  );
  const label = (
    <span className={`text-[16px] font-semibold text-white leading-[1.2] ${isLeftGroup ? "text-right" : "text-left"}`}>
      {cap.label}
    </span>
  );
  return (
    <li
      className={`flex items-center min-h-[52px] ${isLeftGroup ? "justify-end" : "justify-start"}`}
      style={{ gap: "8px" }}
      data-testid={testid}
    >
      {isLeftGroup ? (<>{label}{iconTile}</>) : (<>{iconTile}{label}</>)}
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────
//  BETTER WAY · Right zone column · authorized 2026-02
//  Clean, bright, professional contractor-workspace tone rendered
//  natively (no image asset — only live React/CSS). Sits inside the
//  same continuous navy environment, using JobClose blue as the
//  primary visual language.
// ─────────────────────────────────────────────────────────────────
const BetterWayColumn = ({ better }) => {
  return (
    <div className="min-w-0 justify-self-start w-full max-w-[432px] 2xl:-translate-x-[44px]" data-testid="hiw-p2-better-way">
      {/* Header · badge · title · sub */}
      <div className="flex flex-col items-center text-center">
        <span
          className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--jc-blue)] text-white text-[18px] font-extrabold shadow-[0_10px_30px_-8px_rgba(59,130,246,0.9)] ring-4 ring-[var(--jc-blue)]/20"
          data-testid="hiw-p2-better-badge"
        >
          3
        </span>
        <h2
          className="mt-5 font-display font-extrabold tracking-tight text-white text-[52px] leading-[1.02]"
          data-testid="hiw-p2-better-title"
        >
          {better.title}
        </h2>
        <p
          className="mt-3 text-[18px] font-medium leading-[1.5] max-w-[440px]"
          style={{ color: "rgba(191,219,254,0.85)" }}
          data-testid="hiw-p2-better-sub"
        >
          {better.sub}
        </p>
      </div>

      {/* Contractor proposal · native React · bright organized tone */}
      <div className="relative mt-6" data-testid="hiw-p2-better-proposal-frame">
        {/* Bright bloom behind the document */}
        <div
          aria-hidden="true"
          className="absolute -inset-3 rounded-[28px] blur-2xl opacity-95"
          style={{
            background:
              "radial-gradient(60% 60% at 50% 55%, rgba(147,197,253,0.32) 0%, rgba(59,130,246,0.16) 42%, rgba(8,18,44,0) 78%)",
          }}
        />
        <ContractorProposal better={better} />
      </div>

      {/* Six outcomes · 2-col live text · blue-checked tiles */}
      <ul
        className="mt-8 grid grid-cols-2 gap-x-5 gap-y-4"
        data-testid="hiw-p2-better-outcomes"
      >
        {better.outcomes.map((o, i) => (
          <li
            key={i}
            className="flex items-center gap-3"
            data-testid={`hiw-p2-better-outcome-${i}`}
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[var(--jc-blue)]/18 text-[#93C5FD] ring-1 ring-[var(--jc-blue)]/35 flex-shrink-0">
              <CheckCircle2 size={16} strokeWidth={2.4} />
            </span>
            <span className="text-[16px] font-semibold text-white/95 leading-tight">
              {o}
            </span>
          </li>
        ))}
      </ul>

      {/* Logo affirmation callout */}
      <div
        className="mt-6 flex items-start gap-3 rounded-2xl bg-white/6 ring-1 ring-white/12 backdrop-blur-sm px-4 py-3.5"
        data-testid="hiw-p2-better-logo-note"
      >
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[var(--jc-blue)]/25 text-[#93C5FD] ring-1 ring-[var(--jc-blue)]/40 flex-shrink-0 mt-[1px]">
          <BadgeCheck size={17} strokeWidth={2.4} />
        </span>
        <p className="text-[14px] leading-[1.45] font-semibold text-white/90">
          {better.logo_msg}
        </p>
      </div>
    </div>
  );
};

// Contractor proposal document (fictional: Valley Painting)
const ContractorProposal = ({ better }) => (
  <div
    className="relative block w-full rounded-2xl bg-white overflow-hidden shadow-[0_28px_60px_-20px_rgba(0,0,0,0.85)] ring-1 ring-white/20"
    data-testid="hiw-p2-better-proposal"
  >
    {/* Contractor branded header strip */}
    <div className="flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#0B2447] to-[var(--jc-blue)] text-white">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-lg bg-white/15 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0">
          <PaintBucket size={22} strokeWidth={2.4} />
        </div>
        <div className="min-w-0">
          <div className="text-[16px] font-extrabold tracking-tight leading-tight truncate">VALLEY PAINTING</div>
          <div className="text-[10px] font-semibold tracking-[0.14em] text-white/80 leading-tight mt-0.5">{better.company_tagline}</div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 ring-1 ring-white/25 px-2.5 py-1 text-[10.5px] font-bold tracking-wide">
          <BadgeCheck size={12} strokeWidth={2.6} />
          {better.proposal_badge}
        </div>
      </div>
    </div>

    {/* Meta strip */}
    <div className="px-5 py-3.5 grid grid-cols-3 gap-3 border-b border-black/5 bg-[#F7FAFF]">
      <div>
        <div className="text-[10px] font-semibold text-[var(--jc-text-soft)] uppercase tracking-wider">{better.labels.project}</div>
        <div className="mt-0.5 text-[13px] font-bold text-[var(--jc-text)] leading-tight">2405 Maple Ave.</div>
      </div>
      <div>
        <div className="text-[10px] font-semibold text-[var(--jc-text-soft)] uppercase tracking-wider">{better.labels.customer}</div>
        <div className="mt-0.5 text-[13px] font-bold text-[var(--jc-text)] leading-tight">Martha Johnson</div>
      </div>
      <div>
        <div className="text-[10px] font-semibold text-[var(--jc-text-soft)] uppercase tracking-wider">{better.labels.date}</div>
        <div className="mt-0.5 text-[13px] font-bold text-[var(--jc-text)] leading-tight">{better.date_val}</div>
      </div>
    </div>

    {/* Scope section */}
    <div className="px-5 py-4">
      <div className="flex items-center justify-between">
        <div className="text-[14.5px] font-extrabold text-[var(--jc-text)] tracking-tight">{better.scope_title}</div>
        <div className="text-[11px] font-semibold text-[var(--jc-blue)]">{better.labels.scope}</div>
      </div>
      <ul className="mt-3 space-y-2">
        {better.scope.map((s, i) => (
          <li key={i} className="flex items-center gap-2 text-[12.5px] text-[var(--jc-text)]">
            <CheckCircle2 size={14} className="text-[var(--jc-blue)] flex-shrink-0" strokeWidth={2.4} />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>

    {/* Line items summary */}
    <div className="px-5 pb-4">
      <div className="rounded-xl bg-[#F7FAFF] ring-1 ring-black/5 p-3.5">
        <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
          <span className="text-[var(--jc-text-soft)]">{better.labels.materials}</span>
          <span className="text-right font-bold text-[var(--jc-text)]">$2,340.00</span>
          <span className="text-[var(--jc-text-soft)]">{better.labels.labor}</span>
          <span className="text-right font-bold text-[var(--jc-text)]">$3,890.00</span>
          <span className="text-[var(--jc-text-soft)]">{better.labels.prep}</span>
          <span className="text-right font-bold text-[var(--jc-text)]">$645.00</span>
        </div>
      </div>
    </div>

    {/* Total footer */}
    <div className="border-t border-black/5 px-5 py-3.5 flex items-center justify-between bg-gradient-to-r from-[var(--jc-blue-soft)] to-white">
      <div>
        <div className="text-[10.5px] font-semibold text-[var(--jc-text-soft)] uppercase tracking-wider">{better.labels.total}</div>
      </div>
      <div className="text-[22px] font-extrabold text-[var(--jc-blue)] tracking-tight">$6,875.00</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
//  FULL PART 2 (preserved verbatim — hidden while SHOW_FULL_PART2
//  is false; flip to true to restore).
// ─────────────────────────────────────────────────────────────────
const FullPart2 = ({ c }) => (
  <section className="relative bg-gradient-to-b from-white via-[#F5F8FF] to-white" data-testid="hiw-part2">
    <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-14">
      {/* Section head */}
      <div className="text-center max-w-4xl mx-auto" data-testid="hiw-p2-head">
        <div className="inline-block px-3.5 py-1 rounded-full bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] text-[12px] font-bold tracking-[0.14em]">{c.kicker}</div>
        <h2 className="mt-5 font-display font-extrabold tracking-tight text-[var(--jc-text)] text-[36px] sm:text-[46px] lg:text-[58px] leading-[1.05]">{c.headline}</h2>
        <p className="mt-5 text-[16px] sm:text-[18px] leading-[1.55] text-[var(--jc-text-soft)] font-medium max-w-3xl mx-auto">{c.lede}</p>
      </div>

      {/* 3-step grid */}
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch gap-6 lg:gap-4">
        {/* STEP 1 · Old Way */}
        <StepCard variant="old" step={c.step1}>
          <div className="rounded-xl overflow-hidden border border-black/5 shadow-inner bg-[#EFE7DA]">
            <img src="/brand/how-it-works/hiw_part2_notebook.png" alt="" className="w-full h-auto block" draggable={false} />
          </div>
          <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2.5">
            {c.step1.bullets.map((b, i) => (
              <li key={i} className="flex items-center gap-2 text-[13.5px] text-[var(--jc-text)] font-medium">
                <XCircle size={16} className="text-red-500 flex-shrink-0" strokeWidth={2.4} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </StepCard>

        <Arrow />

        {/* STEP 2 · Levi Organizes It */}
        <StepCard variant="levi" step={c.step2}>
          <div className="rounded-xl bg-gradient-to-b from-[#EAF2FF] to-white p-4 flex flex-col items-center">
            <img src="/brand/how-it-works/levi_headshot_approved.png" alt="Levi" className="w-full max-w-[220px] h-auto block select-none" draggable={false} />
          </div>
          <ul className="mt-4 space-y-2.5">
            {c.step2.caps.map((cap, i) => {
              const Icon = cap.Icon;
              return (
                <li key={i} className="flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] flex-shrink-0"><Icon size={14} strokeWidth={2.2} /></span>
                  <span className="text-[13.5px] font-semibold text-[var(--jc-text)]">{cap.label}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 rounded-xl bg-[var(--jc-blue)] text-white text-center px-4 py-3.5 shadow-lg">
            <div className="text-[13.5px] font-bold leading-tight">{c.step2.promise_a}</div>
            <div className="text-[13.5px] font-medium leading-tight opacity-95 mt-0.5">{c.step2.promise_b}</div>
          </div>
        </StepCard>

        <Arrow />

        {/* STEP 3 · Better Way */}
        <StepCard variant="better" step={c.step3}>
          <ProposalMock p={c.step3.proposal} />
          <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            {c.step3.outcomes.map((o, i) => (
              <li key={i} className="flex items-center gap-2 text-[13px] font-semibold text-[var(--jc-text)]">
                <CheckCircle2 size={16} className="text-[var(--jc-blue)] flex-shrink-0" strokeWidth={2.4} />
                <span>{o}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[var(--jc-blue-soft)] px-3.5 py-3">
            <BadgeCheck size={18} className="text-[var(--jc-blue)] mt-0.5 flex-shrink-0" strokeWidth={2.4} />
            <p className="text-[12.5px] leading-[1.45] font-semibold text-[var(--jc-text)]">{c.step3.logo_note}</p>
          </div>
        </StepCard>
      </div>

      {/* Trades + Custom Job row */}
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        {/* Trades */}
        <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-6 lg:p-8" data-testid="hiw-p2-trades">
          <h3 className="font-display font-bold text-[20px] text-[var(--jc-text)]">{c.trades.title}</h3>
          <p className="mt-1.5 text-[14px] text-[var(--jc-text-soft)]">{c.trades.subtitle}</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {c.trades.items.map((t, i) => {
              const Icon = t.Icon;
              return (
                <div key={i} className="flex flex-col items-center text-center gap-2 px-2 py-3 rounded-xl hover:bg-[var(--jc-blue-soft)]/60 transition-colors">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[var(--jc-blue-soft)] text-[var(--jc-blue)]"><Icon size={22} strokeWidth={2} /></span>
                  <span className="text-[12.5px] font-semibold text-[var(--jc-text)] leading-tight">{t.label}</span>
                </div>
              );
            })}
            <div className="flex flex-col items-center text-center gap-2 px-2 py-3 rounded-xl border border-dashed border-[var(--jc-blue)]/30 bg-[var(--jc-blue-soft)]/30">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white text-[var(--jc-blue)] border border-[var(--jc-blue)]/20"><Tag size={20} strokeWidth={2} /></span>
              <span className="text-[12.5px] font-semibold text-[var(--jc-blue)] leading-tight">{c.trades.more}</span>
            </div>
          </div>
        </div>

        {/* Custom Job */}
        <div className="rounded-2xl bg-gradient-to-br from-[#FFF8E1] to-[#FFF3C4] border border-amber-200/60 shadow-sm p-6 lg:p-7 flex flex-col" data-testid="hiw-p2-custom">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-400/25 text-amber-700"><Tag size={18} strokeWidth={2.2} /></span>
            <h3 className="font-display font-bold text-[19px] text-[var(--jc-text)]">{c.custom.title}</h3>
          </div>
          <p className="mt-3 text-[14px] leading-[1.55] text-[var(--jc-text)] font-medium">{c.custom.body}</p>
          <div className="mt-auto pt-4">
            <div className="inline-block w-full text-center rounded-lg bg-amber-400/95 text-[var(--jc-text)] px-4 py-3 font-bold text-[13.5px] shadow-sm">{c.custom.pill}</div>
          </div>
        </div>
      </div>

      {/* Consolidation banner */}
      <div className="mt-12 rounded-2xl bg-white border border-black/5 shadow-sm p-5 lg:p-6 flex items-center gap-4" data-testid="hiw-p2-consolidation">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[var(--jc-blue-soft)] text-[var(--jc-blue)] flex-shrink-0"><ShieldCheck size={22} strokeWidth={2.2} /></span>
        <p className="text-[15px] sm:text-[16.5px] leading-[1.5] font-semibold text-[var(--jc-text)]">{c.consolidation}</p>
      </div>
    </div>
  </section>
);

const Arrow = () => (
  <div className="hidden lg:flex items-center justify-center px-1">
    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-white border border-black/5 shadow-md text-[var(--jc-blue)]"><ArrowRight size={22} strokeWidth={2.4} /></span>
  </div>
);

const StepCard = ({ variant, step, children }) => {
  const bg =
    variant === "old" ? "bg-white border-black/5" :
    variant === "levi" ? "bg-white border-[var(--jc-blue)]/15 ring-1 ring-[var(--jc-blue)]/10" :
    "bg-white border-black/5";
  const badgeColor =
    variant === "old" ? "bg-[var(--jc-text)]" :
    variant === "levi" ? "bg-[var(--jc-blue)]" :
    "bg-[var(--jc-blue)]";
  const titleColor = variant === "levi" || variant === "better" ? "text-[var(--jc-blue)]" : "text-[var(--jc-text)]";
  return (
    <div className={`rounded-2xl border shadow-sm p-5 lg:p-6 flex flex-col ${bg}`}>
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-[15px] font-bold ${badgeColor}`}>{step.badge}</span>
        <h3 className={`font-display font-extrabold text-[22px] leading-tight ${titleColor}`}>{step.title}</h3>
      </div>
      <p className="mt-1.5 text-[13.5px] text-[var(--jc-text-soft)] font-medium">{step.sub}</p>
      <div className="mt-4 flex-1">{children}</div>
    </div>
  );
};

const ProposalMock = ({ p }) => (
  <div className="rounded-xl bg-white border border-black/10 shadow-md overflow-hidden">
    <div className="px-4 pt-4 pb-3">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-lg bg-[var(--jc-blue)] text-white flex items-center justify-center flex-shrink-0"><PaintBucket size={20} strokeWidth={2.4} /></div>
        <div className="min-w-0">
          <div className="text-[13px] font-extrabold text-[var(--jc-text)] tracking-tight leading-tight truncate">{p.company_line1}</div>
          <div className="text-[9px] font-semibold text-[var(--jc-blue)] tracking-[0.08em] leading-tight mt-0.5 truncate">{p.company_line2}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-1.5 text-[9.5px] leading-tight">
        <div><div className="text-[var(--jc-text-soft)] font-medium">{p.project_label}</div><div className="font-bold text-[var(--jc-text)] truncate">{p.project_val}</div></div>
        <div><div className="text-[var(--jc-text-soft)] font-medium">{p.date_label}</div><div className="font-bold text-[var(--jc-text)] truncate">{p.date_val}</div></div>
        <div><div className="text-[var(--jc-text-soft)] font-medium">{p.customer_label}</div><div className="font-bold text-[var(--jc-text)] truncate">{p.customer_val}</div></div>
      </div>
    </div>
    <div className="border-t border-black/5 px-4 py-3">
      <div className="text-[12.5px] font-bold text-[var(--jc-text)] mb-2">{p.scope_title}</div>
      <ul className="space-y-1">
        {p.scope.map((s, i) => (
          <li key={i} className="flex items-center gap-1.5 text-[11.5px] text-[var(--jc-text)]">
            <CheckCircle2 size={12} className="text-[var(--jc-blue)] flex-shrink-0" strokeWidth={2.4} />
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="border-t border-black/5 px-4 py-2.5 flex items-center justify-between bg-[var(--jc-blue-soft)]/40">
      <span className="text-[11px] font-semibold text-[var(--jc-text)]">{p.total_label}</span>
      <span className="text-[15px] font-extrabold text-[var(--jc-blue)]">{p.total_val}</span>
    </div>
  </div>
);

export default HowItWorksPart2;
